import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import adminAllStoresService from '../../../services/adminAllStoresService';
import type { SubscriptionStatus } from '../../../types/subscriptionStatus';
import type { FeatureFlagStoreOption } from '../../../types/features/featureFlags';
import {
  filterFeatureFlagStoreOptions,
  mapAdminStoreToFeatureFlagStoreOption,
} from '../mappers/featureFlagStoreOptionMapper';

const DEFAULT_FEATURE_FLAG_STORE_STATUS_FILTER: SubscriptionStatus = 'active';

export const useFeatureFlagStoreOptions = (enabled: boolean) => {
  const requestIdRef = useRef(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<SubscriptionStatus>(DEFAULT_FEATURE_FLAG_STORE_STATUS_FILTER);
  const [stores, setStores] = useState<FeatureFlagStoreOption[]>([]);
  const debouncedSearch = useDebouncedValue(search, 300);

  const filteredStores = useMemo(
    () => filterFeatureFlagStoreOptions(stores, debouncedSearch),
    [debouncedSearch, stores],
  );

  const fetchStores = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError('');

    try {
      const response = await adminAllStoresService.getAllStores({
        subscription_status: statusFilter,
      });

      if (requestId === requestIdRef.current) {
        setStores(response.stores.map(mapAdminStoreToFeatureFlagStoreOption));
      }
    } catch (requestError: any) {
      if (requestId === requestIdRef.current) {
        setError(requestError?.message || 'Failed to load stores');
        setStores([]);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [statusFilter]);

  useEffect(() => {
    if (enabled) {
      fetchStores();
    }
  }, [enabled, fetchStores]);

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
  }, []);

  const handleStatusFilterChange = useCallback((value: SubscriptionStatus) => {
    setStatusFilter(value);
  }, []);

  const handleResetFilters = useCallback(() => {
    setSearch('');
    setStatusFilter(DEFAULT_FEATURE_FLAG_STORE_STATUS_FILTER);
  }, []);

  return {
    error,
    handleResetFilters,
    handleSearchChange,
    handleStatusFilterChange,
    loading,
    search,
    statusFilter,
    stores: filteredStores,
  };
};
